import { useMemo } from "react";

import { Box, Grid, Typography } from "@mui/material";

import { LoadingOverlay, MeatballMenu } from "@covertree/ui";

import { formatAwsDateString, formatCurrency, slugify } from "@covertree/utils";

import { useAccessPattern } from "@hooks/useAccessPattern";
import { useRetryRenewalIssuance } from "@hooks/actions/useRetryRenewalIssuance";
import DisplayPropertiesCard from "@covertree/ui/content/DisplayPropertiesCard";
import { mapPaymentMethod } from "@utils/mapPaymentMethod";
import { useOpenRenewalDetails } from "@hooks/actions/useOpenRenewalDetails";
import { mapRenewalStatus } from "@utils/mapRenewalStatus";
import {
  RenewalStatus,
  RenewalWithAgencyDataFragment,
  RenewalWithCheckReceivalFragment,
} from "@covertree/agent-portal-graphql";

import { mapUWReviewToDescription } from "./utils/mapUWReviewToDescription";
import { useCheckReceivalPermissions } from "@hooks/actions/useCheckReceivalPermissions";
import { useRenewalActions } from "@providers/RenewalActionsProvider";
import { useRetryRenewalSendOffer } from "@hooks/actions/useRetryRenewalSendOffer";
import { useRetryFailedRenewal } from "@hooks/actions/useRetryFailedRenewal";
import { useResendRenewalOffer } from "@hooks/actions/useResendRenewalOffer";

interface RenewalDetailsProps {
  renewal: RenewalWithCheckReceivalFragment | RenewalWithAgencyDataFragment;
}

export const isRenewalWithCheckReceivalData = (
  r: unknown
): r is RenewalWithCheckReceivalFragment => {
  const renewal = r as RenewalWithCheckReceivalFragment;

  return typeof renewal.checkReceival?.createdTimestamp !== "undefined";
};

export function RenewalDetails({ renewal }: RenewalDetailsProps) {
  const { canRetryFailedRenewal, canSeeDiscardedRenewals } = useAccessPattern();
  const openRenewalDetails = useOpenRenewalDetails();
  const { retryIssuance, loading: isRetryIssuanceLoading } =
    useRetryRenewalIssuance();

  const { canMarkRenewalAsPaid, canMarkRenewalAsUnpaid } =
    useCheckReceivalPermissions(renewal);
  const { retryRenewalSendOffer, loading: isRetrySendOfferLoading } =
    useRetryRenewalSendOffer();
  const { retryFailedRenewal, loading: isRetryFailedRenewalLoading } =
    useRetryFailedRenewal();
  const { resendRenewalOffer, loading: isResendRenewalOfferLoading } =
    useResendRenewalOffer();

  const {
    showMarkAsPaidModal,
    showMarkAsUnpaidModal,
    showRenewalDiscardModal,
  } = useRenewalActions();

  const isAnythingLoading = useMemo(
    () =>
      isRetrySendOfferLoading ||
      isRetryIssuanceLoading ||
      isRetryFailedRenewalLoading ||
      isResendRenewalOfferLoading,
    [
      isRetrySendOfferLoading,
      isRetryIssuanceLoading,
      isRetryFailedRenewalLoading,
      isResendRenewalOfferLoading,
    ]
  );

  const isRenewalDiscarded = useMemo(
    () => renewal.status === RenewalStatus.Discarded,
    [renewal.status]
  );

  const isUWReviewRenewal = useMemo(
    () => renewal?.status === RenewalStatus.UwReview,
    [renewal?.status]
  );

  const isNonRenewal = useMemo(
    () => renewal?.status === RenewalStatus.NonRenewalIssued,
    [renewal?.status]
  );

  const renewalActions = useMemo(
    () => [
      {
        label: "View Details",
        onClick: () => openRenewalDetails(renewal.renewalLocator),
      },
      ...(canMarkRenewalAsPaid
        ? [
            {
              label: "Mark as Paid",
              onClick: () =>
                showMarkAsPaidModal(
                  renewal as RenewalWithCheckReceivalFragment
                ),
            },
          ]
        : []),
      ...(canMarkRenewalAsUnpaid
        ? [
            {
              label: "Edit Invoice Settlement Description",
              onClick: () =>
                showMarkAsPaidModal(
                  renewal as RenewalWithCheckReceivalFragment
                ),
            },
            {
              label: "Mark as Unpaid",
              onClick: () =>
                showMarkAsUnpaidModal(
                  renewal as RenewalWithCheckReceivalFragment
                ),
            },
          ]
        : []),
      ...(canRetryFailedRenewal &&
      renewal?.status === RenewalStatus.OfferSentFailed
        ? [
            {
              label: "Retry Send Offer",
              onClick: () => retryRenewalSendOffer(renewal.renewalLocator),
            },
          ]
        : []),
      ...(canRetryFailedRenewal &&
      renewal?.status === RenewalStatus.PaymentFailed
        ? [
            {
              label: "Retry Issuance",
              onClick: () => retryIssuance(renewal.renewalLocator),
            },
          ]
        : []),
      ...(canSeeDiscardedRenewals && renewal?.status === RenewalStatus.OfferSent
        ? [
            {
              label: "Discard Renewal",
              onClick: () => showRenewalDiscardModal(renewal.renewalLocator),
            },
          ]
        : []),
      ...(canRetryFailedRenewal && renewal?.status === RenewalStatus.OfferSent
        ? [
            {
              label: "Resend Offer",
              onClick: () => resendRenewalOffer(renewal.renewalLocator),
            },
          ]
        : []),
      ...(canRetryFailedRenewal &&
      [RenewalStatus.PricingFailed, RenewalStatus.UwFailed].includes(
        renewal.status
      )
        ? [
            {
              label: "Retry",
              onClick: () => retryFailedRenewal(renewal.renewalLocator),
            },
          ]
        : []),
    ],
    [
      renewal,
      openRenewalDetails,
      canMarkRenewalAsPaid,
      canMarkRenewalAsUnpaid,
      canRetryFailedRenewal,
      showMarkAsPaidModal,
      showMarkAsUnpaidModal,
    ]
  );

  const descriptionItems: string[] = useMemo(
    () => mapUWReviewToDescription(renewal),
    [renewal]
  );

  const canSeeCheckReceivalInfo = useMemo(
    () =>
      canMarkRenewalAsUnpaid &&
      isRenewalWithCheckReceivalData(renewal) &&
      !!renewal?.checkReceival,
    [canMarkRenewalAsUnpaid, renewal]
  );

  return (
    <Grid item xs={12}>
      <DisplayPropertiesCard
        properties={[
          {
            label: "Renewal Locator",
            value: `#${renewal.renewalLocator}`,
          },
          {
            label: "Renewal Status",
            value: mapRenewalStatus(renewal.status),
          },
          ...(!isNonRenewal
            ? [
                {
                  label: "Payment Method",
                  value: mapPaymentMethod(renewal.paymentMethod),
                },
              ]
            : []),
          ...(canSeeCheckReceivalInfo
            ? [
                {
                  label: "Invoice Settlement Description",
                  value:
                    (renewal as RenewalWithCheckReceivalFragment).checkReceival
                      ?.description || "None",
                  gridSize: 12,
                },
              ]
            : []),
          ...(!isNonRenewal
            ? [
                {
                  label: "Create Date",
                  value: formatAwsDateString(renewal.createdDate),
                },
              ]
            : []),
          {
            label: isNonRenewal ? "Non-Renewal Issue Date" : "Issue Date",
            value: isNonRenewal
              ? formatAwsDateString(renewal.nonRenewalIssueDate)
              : renewal.issuedDate
                ? formatAwsDateString(renewal.issuedDate)
                : "N/A",
          },
          ...(!isNonRenewal
            ? [
                {
                  label: "Renewal Term",
                  value: [
                    formatAwsDateString(renewal.renewalTerm.effectiveDate),
                    formatAwsDateString(renewal.renewalTerm.endDate),
                  ].join(" - "),
                },
                {
                  label: "Renewal Total Due",
                  value: renewal.totalDue
                    ? formatCurrency(renewal.totalDue)
                    : "N/A",
                },
                {
                  label: "Renewal Premium",
                  value: renewal.premium
                    ? formatCurrency(renewal.premium)
                    : "N/A",
                },
              ]
            : [
                {
                  label: "Non-Renewal Reason",
                  value: renewal.nonRenewalReason || "N/A",
                  gridSize: 12,
                },
              ]),
          ...(isRenewalDiscarded
            ? [
                {
                  label: "Discarded Date",
                  value: renewal?.discardedDate
                    ? formatAwsDateString(renewal?.discardedDate)
                    : "N/A",
                },
                {
                  label: "Discarded By",
                  value: renewal?.discardedBy || "N/A",
                  gridSize: 6,
                },
              ]
            : []),
          {
            label: isNonRenewal
              ? "Non-Renewal Description"
              : "Renewal Description",
            value: isUWReviewRenewal ? (
              <Box component="ul" mt={0} mb={0} ml={-2}>
                {descriptionItems.map((reason) => (
                  <Box key={slugify(reason!)} component="li">
                    <Typography>{reason}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              renewal?.description || "None"
            ),
            gridSize: 12,
          },
          {
            label: "Assigned Agent",
            value: renewal?.assignedAgentName ?? "Not Assigned",
            gridSize: 6,
          },
          {
            label: "Assigned Agency",
            value: renewal?.assignedAgencyName ?? "Not Assigned",
            gridSize: 6,
          },
        ]}>
        {renewalActions.length ? (
          <MeatballMenu actionMenuItems={renewalActions} border closeOnClick />
        ) : null}
        {isAnythingLoading && <LoadingOverlay />}
      </DisplayPropertiesCard>
    </Grid>
  );
}
